'use client';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Skeleton } from '@/components/ui/skeleton';
import { Slider } from '@/components/ui/slider';
import { cn } from '@/lib/utils';
import { CheckIcon, Loader2, PauseIcon, PlayIcon } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useAudioPreview } from '../hooks/use-audio-preview';

/** A track the editor can offer — `url` must be absolute and playable. */
export interface EditableMusicTrack {
  id: string;
  name: string;
  artist?: string;
  durationSec: number;
  url: string;
  /**
   * Set for the organization's own uploads: the stored file URL a draft saves
   * as `musicUrl` (with `musicTrackId` = {@link UPLOADED_TRACK_ID}). `url` is
   * then only a short-lived link for listening.
   */
  saveUrl?: string;
  /** Uploads only: where the best part starts, saved with the choice. */
  startSec?: number;
}

export interface MusicChoice {
  /** `null` = no music; {@link UPLOADED_TRACK_ID} = one of the org's uploads. */
  trackId: string | null;
  url: string | null;
  /** 0–1. */
  volume: number;
  /** Uploads only: seconds into the track the video's music starts at. */
  startSec?: number;
}

/** `musicTrackId` for music the organization uploaded itself. */
export const UPLOADED_TRACK_ID = 'uploaded';

const clock = (totalSec: number) => {
  const sec = Math.round(totalSec);
  return `${Math.floor(sec / 60)}:${String(sec % 60).padStart(2, '0')}`;
};

/** The row a saved choice points at: a library id, or an upload by its file. */
function rowIdFor(value: MusicChoice, tracks: EditableMusicTrack[]) {
  if (value.trackId !== UPLOADED_TRACK_ID) return value.trackId;
  return tracks.find((t) => t.saveUrl && t.saveUrl === value.url)?.id ?? null;
}

function trackDetail(track: EditableMusicTrack) {
  if (track.saveUrl) {
    return track.durationSec > 0
      ? `Your upload · ${clock(track.durationSec)}`
      : 'Your upload';
  }
  return `${track.artist ? `${track.artist} · ` : ''}${clock(track.durationSec)}`;
}

/**
 * "Edit music": pick a background track — the shared library or one of the
 * organization's uploads (added on the Music page) — listen first, set its
 * volume, then save. Shared by the create-video wizard and Claire's video
 * draft card; each caller decides where the choice is written.
 */
export function MusicEditorDialog({
  open,
  onOpenChange,
  tracks,
  isLoading,
  value,
  onSave,
  saving,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  tracks: EditableMusicTrack[];
  isLoading?: boolean;
  value: MusicChoice;
  onSave: (choice: MusicChoice) => void | Promise<void>;
  saving?: boolean;
}) {
  const [rowId, setRowId] = useState<string | null>(() =>
    rowIdFor(value, tracks)
  );
  const [volume, setVolume] = useState(value.volume);
  const { playingId, toggle, stop } = useAudioPreview();

  // Reset only when the dialog opens, the SAVED choice changes, or the track
  // list changes (uploads arriving) — keyed on primitives. Callers rebuild
  // `value` and `tracks` on every render (the draft card polls every 5s), and
  // keying on those objects wiped the owner's in-progress pick each render.
  const trackIds = tracks.map((t) => t.id).join('|');
  // biome-ignore lint/correctness/useExhaustiveDependencies: keyed on the primitives above on purpose
  useEffect(() => {
    if (open) {
      setRowId(rowIdFor(value, tracks));
      setVolume(value.volume);
    } else {
      stop();
    }
  }, [open, value.trackId, value.url, value.volume, trackIds, stop]);

  const selected = tracks.find((t) => t.id === rowId) ?? null;

  const handleSave = async () => {
    stop();
    if (!selected) {
      await onSave({ trackId: null, url: null, volume });
      return;
    }
    await onSave(
      selected.saveUrl
        ? {
            trackId: UPLOADED_TRACK_ID,
            url: selected.saveUrl,
            volume,
            ...(selected.startSec !== undefined
              ? { startSec: selected.startSec }
              : {}),
          }
        : { trackId: selected.id, url: selected.url, volume }
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Edit music</DialogTitle>
          <DialogDescription>
            Pick a background track and how loud it plays. Upload your own on
            the Music page.
          </DialogDescription>
        </DialogHeader>

        <div className="max-h-[320px] space-y-1.5 overflow-y-auto">
          {isLoading ? (
            ['a', 'b', 'c'].map((k) => (
              <Skeleton key={k} className="h-12 w-full rounded-lg" />
            ))
          ) : (
            <>
              <button
                type="button"
                aria-pressed={rowId === null}
                onClick={() => setRowId(null)}
                className={cn(
                  'flex w-full items-center gap-3 rounded-lg border px-3 py-2.5 text-left text-sm',
                  rowId === null
                    ? 'border-primary bg-primary/5'
                    : 'hover:bg-muted/50'
                )}
              >
                <span className="flex-1">No music</span>
                {rowId === null && (
                  <CheckIcon className="size-4 text-primary" />
                )}
              </button>
              {tracks.map((track) => {
                const isSelected = track.id === rowId;
                const isPlaying = playingId === track.id;
                return (
                  <div
                    key={track.id}
                    className={cn(
                      'flex items-center gap-2 rounded-lg border pr-2',
                      isSelected
                        ? 'border-primary bg-primary/5'
                        : 'hover:bg-muted/50'
                    )}
                  >
                    <button
                      type="button"
                      aria-pressed={isSelected}
                      onClick={() => setRowId(track.id)}
                      className="flex min-w-0 flex-1 items-center gap-3 px-3 py-2 text-left"
                    >
                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-sm font-medium">
                          {track.name}
                        </span>
                        <span className="block truncate text-xs text-muted-foreground">
                          {trackDetail(track)}
                        </span>
                      </span>
                      {isSelected && (
                        <CheckIcon className="size-4 shrink-0 text-primary" />
                      )}
                    </button>
                    <Button
                      type="button"
                      size="icon"
                      variant="ghost"
                      className="size-8 shrink-0 rounded-full"
                      aria-label={
                        isPlaying
                          ? `Pause ${track.name}`
                          : `Listen to ${track.name}`
                      }
                      onClick={() => toggle(track.id, track.url)}
                    >
                      {isPlaying ? (
                        <PauseIcon className="size-4" />
                      ) : (
                        <PlayIcon className="size-4" />
                      )}
                    </Button>
                  </div>
                );
              })}
            </>
          )}
        </div>

        <div className="space-y-2">
          <div className="flex items-center justify-between text-sm">
            <span id="music-editor-volume">Volume</span>
            <span className="tabular-nums text-muted-foreground">
              {Math.round(volume * 100)}%
            </span>
          </div>
          <Slider
            aria-labelledby="music-editor-volume"
            min={0}
            max={100}
            step={5}
            disabled={!selected}
            value={[Math.round(volume * 100)]}
            onValueChange={([v]) => setVolume((v ?? 0) / 100)}
          />
        </div>

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={saving}
          >
            Cancel
          </Button>
          <Button type="button" onClick={handleSave} disabled={saving}>
            {saving && <Loader2 className="size-4 animate-spin" />}
            Save music
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
